import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import { KeyboardDatePicker } from '@material-ui/pickers'
import { Link } from 'react-router-dom'
import { toast } from 'react-toastify'
import Alert from '@material-ui/lab/Alert'

import { axiosInstance } from '../../../helpers'
import { Routes } from '../../../navigation/config/routes'

import { usePropertyLeasingSeasonConfigs } from '../../LeasingSeasonConfig/useLeasingSeasonConfigApi'
import { useLeaseTerms } from '../../../hooks/useLeaseTerms'
import { DEFAULT_SEASON_CONFIG } from '../constants'
import { SEASONS_LIST_KEY } from '../hooks/leasingSeasonKeys'
import {
    DemoSeasonChoice,
    DemoSeasonProgress,
    DemoSeedStatus,
} from './DemoSeasonChoice'
import {
    parseSeasonDate,
    seasonDateFromPicker,
    seasonDatesAreOrdered,
    seasonDatesFromTermStart,
} from '../utils/seasonDates'
import {
    LeasingSeasonConfig,
    LeasingSeasonListItem,
} from '../types/leasingSeason.types'
import {
    LeaseTermApplyFields,
    LeaseTermPropertyDetailsPrompt,
} from './LeaseTermApplyFields'
import { SeasonConfigSourcePanel } from './SeasonConfigSourcePanel'
import { SeasonLeadRolloverPanel } from './SeasonLeadRolloverPanel'

const useStyles = makeStyles((theme: Theme) => ({
    field: {
        width: '100%',
    },
    pipelineNote: {
        color: theme.palette.text.secondary,
        fontSize: '0.8125rem',
        marginTop: theme.spacing(0.5),
    },
    demoChoiceNote: {
        marginBottom: theme.spacing(1),
    },
    termGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1.5),
        padding: theme.spacing(2),
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: theme.spacing(1),
    },
}))

export type SeasonFormMode = 'edit' | 'create'

export interface SeasonCreateRolloverOptions {
    sourceSeasonId: number
    leadIds: number[]
}

interface SeasonFormDialogProps {
    open: boolean
    mode: SeasonFormMode
    apartmentId?: number
    seasons?: LeasingSeasonListItem[]
    initialConfig?: LeasingSeasonConfig | null
    onClose: () => void
    onSave?: (config: LeasingSeasonConfig, makeCurrent: boolean) => void
    isCurrentSeason?: boolean
    onCreate?: (
        config: LeasingSeasonConfig,
        makeCurrent: boolean,
        rollover?: SeasonCreateRolloverOptions,
    ) => void | Promise<void>
    onDemoFinished?: (seasonId: number) => void
    isSaving?: boolean
}

function configFromInitial(
    initial: LeasingSeasonConfig | null | undefined,
): LeasingSeasonConfig {
    if (initial) {
        return {
            ...initial,
            allocations: { ...initial.allocations },
        }
    }
    return {
        ...DEFAULT_SEASON_CONFIG,
        allocations: { ...DEFAULT_SEASON_CONFIG.allocations },
    }
}

export const SeasonFormDialog: React.FC<SeasonFormDialogProps> = ({
    open,
    mode,
    apartmentId,
    seasons = [],
    initialConfig,
    onClose,
    onSave,
    onCreate,
    onDemoFinished,
    isSaving,
    isCurrentSeason = false,
}) => {
    const classes = useStyles()
    const queryClient = useQueryClient()
    const sawDemoRunning = useRef(false)
    const [form, setForm] = useState<LeasingSeasonConfig>(() =>
        configFromInitial(initialConfig),
    )
    const [makeCurrent, setMakeCurrent] = useState(false)
    const [rollLeadsEnabled, setRollLeadsEnabled] = useState(false)
    const [rollSourceSeasonId, setRollSourceSeasonId] = useState<number | ''>(
        '',
    )
    const [rollLeadIds, setRollLeadIds] = useState<number[]>([])
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [wantsDemoData, setWantsDemoData] = useState<boolean | null>(null)
    const [demoRunActive, setDemoRunActive] = useState(false)
    const [demoError, setDemoError] = useState('')

    const leasingSeasonConfigsQuery = usePropertyLeasingSeasonConfigs(
        apartmentId,
    )
    const leaseTermsQuery = useLeaseTerms(
        mode === 'create' || mode === 'edit' ? apartmentId : undefined,
    )
    const leaseTerms = leaseTermsQuery.terms ?? []
    const demoStatusQuery = useQuery({
        queryKey: ['demo-season-seed', apartmentId],
        queryFn: async () => {
            const response = await axiosInstance.get<DemoSeedStatus>(
                '/outreach/leasing-seasons/demo-seed/',
            )
            return response.data
        },
        enabled: open && mode === 'create',
        refetchInterval: (data) => (data?.status === 'running' ? 2000 : false),
    })
    const demoStatus = demoStatusQuery.data
    const demoSeedAvailable = Boolean(demoStatus?.enabled)
    const demoRunning =
        demoStatus?.status !== 'failed' &&
        (demoRunActive || demoStatus?.status === 'running')
    const leasingSeasonConfigs = leasingSeasonConfigsQuery.data ?? []
    const readyLeasingSeasonConfigs = useMemo(
        () =>
            leasingSeasonConfigs.filter((config) => {
                if (typeof config.is_ready === 'boolean') {
                    return config.is_ready
                }
                return (
                    config.application_workbook_id != null &&
                    config.guest_card_pack_id != null &&
                    config.fee_policy_id != null &&
                    config.signable_doc_set_id != null &&
                    config.property_data_pack_id != null &&
                    config.pipeline_config_id != null
                )
            }),
        [leasingSeasonConfigs],
    )

    const bundleLocked = mode === 'edit' && !!form.leasingSeasonConfigLocked
    const currentConfigInLibrary = leasingSeasonConfigs.some(
        (config) => config.id === form.leasingSeasonConfigId,
    )
    const seasonHasPrivateCopy =
        mode === 'edit' &&
        form.leasingSeasonConfigId != null &&
        !currentConfigInLibrary

    const sortedSeasons = useMemo(
        () =>
            [...seasons].sort(
                (a, b) =>
                    new Date(b.start_date).getTime() -
                    new Date(a.start_date).getTime(),
            ),
        [seasons],
    )

    useEffect(() => {
        if (open) {
            setForm(configFromInitial(initialConfig))
            setMakeCurrent(false)
            setRollLeadsEnabled(false)
            setRollSourceSeasonId('')
            setRollLeadIds([])
            setIsSubmitting(false)
            setWantsDemoData(null)
            setDemoRunActive(false)
            setDemoError('')
            sawDemoRunning.current = false
        }
    }, [open, initialConfig])

    useEffect(() => {
        if (!open || mode !== 'create') return
        if (form.leasingSeasonConfigId != null) return
        if (readyLeasingSeasonConfigs.length === 1) {
            const only = readyLeasingSeasonConfigs[0]
            setForm((prev) => ({
                ...prev,
                leasingSeasonConfigId: only.id,
                leasingSeasonConfigName: only.name,
            }))
        }
    }, [open, mode, form.leasingSeasonConfigId, readyLeasingSeasonConfigs])

    useEffect(() => {
        if (!open || mode !== 'create') return
        if (demoStatus?.status === 'running') sawDemoRunning.current = true
        if (
            sawDemoRunning.current &&
            demoStatus?.status === 'completed' &&
            demoStatus.season_id
        ) {
            sawDemoRunning.current = false
            setDemoRunActive(false)
            queryClient.invalidateQueries({ queryKey: [SEASONS_LIST_KEY] })
            onDemoFinished?.(demoStatus.season_id)
            onClose()
        }
        if (sawDemoRunning.current && demoStatus?.status === 'failed') {
            sawDemoRunning.current = false
            setDemoRunActive(false)
            setDemoError(
                demoStatus.error || 'Could not create the demo season.',
            )
        }
    }, [demoStatus, mode, onClose, onDemoFinished, open, queryClient])

    const updateForm = (patch: Partial<LeasingSeasonConfig>) => {
        setForm((prev) => ({ ...prev, ...patch }))
    }

    const datesReady = seasonDatesAreOrdered(form.startDate, form.endDate)

    useEffect(() => {
        if (!open || mode !== 'create' || form.leaseTermId == null) return
        const term = leaseTerms.find((row) => row.id === form.leaseTermId)
        const dates = seasonDatesFromTermStart(term?.start_date)
        if (!term || !dates) return
        setForm((prev) => {
            if (prev.leaseTermId !== term.id) return prev
            if (
                prev.startDate === dates.startDate &&
                prev.endDate === dates.endDate
            ) {
                return prev
            }
            return {
                ...prev,
                startDate: dates.startDate,
                endDate: dates.endDate,
            }
        })
    }, [form.leaseTermId, leaseTerms, mode, open])

    const canSubmit = useMemo(() => {
        if (!datesReady) {
            return false
        }
        if (mode !== 'create') {
            return true
        }
        return (
            form.leasingSeasonConfigId != null &&
            form.leaseTermId != null &&
            readyLeasingSeasonConfigs.some(
                (config) => config.id === form.leasingSeasonConfigId,
            )
        )
    }, [
        datesReady,
        form.leasingSeasonConfigId,
        form.leaseTermId,
        mode,
        readyLeasingSeasonConfigs,
    ])

    const handleSave = () => {
        if (!onSave) return
        if (!datesReady) {
            toast.error(
                'Choose a lease term whose move-out is after its move-in.',
            )
            return
        }
        onSave(form, makeCurrent && !isCurrentSeason)
    }

    const handleCreate = async () => {
        if (!onCreate && !wantsDemoData) return
        if (!datesReady) {
            toast.error(
                'Choose a lease term whose move-out is after its move-in.',
            )
            return
        }

        try {
            setIsSubmitting(true)

            const rollover =
                rollLeadsEnabled &&
                rollSourceSeasonId !== '' &&
                rollLeadIds.length > 0
                    ? {
                          sourceSeasonId: rollSourceSeasonId,
                          leadIds: rollLeadIds,
                      }
                    : undefined

            if (form.leasingSeasonConfigId == null) {
                toast.error(
                    'Select a ready Leasing Season Config, or finish one in Leasing Season Config.',
                )
                return
            }

            if (wantsDemoData) {
                setDemoError('')
                await axiosInstance.post(
                    '/outreach/leasing-seasons/demo-seed/',
                    {
                        name: form.name,
                        start_date: form.startDate,
                        end_date: form.endDate,
                        goal_percent: form.goalPercent,
                        lease_term_id: form.leaseTermId,
                        renewal_deadline: form.renewalDeadline || null,
                        renewal_goal_percent: form.renewalGoalPercent ?? null,
                        allocation_prospecting: form.allocations.prospecting,
                        allocation_pipeline: form.allocations.pipeline,
                        allocation_calls: form.allocations.calls,
                        allocation_website: form.allocations.website,
                        make_current: true,
                        leasing_season_config_id: form.leasingSeasonConfigId,
                    },
                )
                setDemoRunActive(true)
                await demoStatusQuery.refetch()
                return
            }

            if (!onCreate) return
            await onCreate(
                {
                    ...form,
                    leasingSeasonConfigId: form.leasingSeasonConfigId,
                },
                true,
                rollover,
            )
        } catch (err: unknown) {
            if (wantsDemoData) {
                const detail = (err as {
                    response?: { data?: { detail?: string } }
                })?.response?.data?.detail
                setDemoError(detail || 'Could not start the demo season.')
                return
            }
            toast.error('Failed to create leasing season.')
        } finally {
            setIsSubmitting(false)
        }
    }

    const applyLeaseTerm = (termId: number) => {
        const term = leaseTerms.find((row) => row.id === termId)
        const dates = seasonDatesFromTermStart(term?.start_date)
        updateForm({
            leaseTermId: termId,
            startDate: dates?.startDate ?? form.startDate,
            endDate: dates?.endDate ?? form.endDate,
        })
    }

    const title =
        mode === 'edit' ? 'Edit leasing season' : 'Create New Leasing Season'

    if (open && mode === 'create' && demoRunning) {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle>{title}</DialogTitle>
                <DialogContent>
                    <DemoSeasonProgress status={demoStatus} />
                </DialogContent>
            </Dialog>
        )
    }

    if (
        open &&
        mode === 'create' &&
        demoSeedAvailable &&
        wantsDemoData === null &&
        !demoStatusQuery.isLoading
    ) {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle>{title}</DialogTitle>
                <DialogContent>
                    {demoError ? (
                        <Typography
                            color="error"
                            className={classes.demoChoiceNote}
                        >
                            {demoError}
                        </Typography>
                    ) : null}
                    <DemoSeasonChoice
                        onYes={() => setWantsDemoData(true)}
                        onNo={() => setWantsDemoData(false)}
                        onClose={onClose}
                    />
                </DialogContent>
            </Dialog>
        )
    }

    if (
        open &&
        mode === 'create' &&
        demoStatusQuery.isLoading &&
        wantsDemoData === null
    ) {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
                <DialogTitle>{title}</DialogTitle>
            </Dialog>
        )
    }

    const isBusy = Boolean(isSaving || isSubmitting)

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>{title}</DialogTitle>
            <DialogContent>
                {wantsDemoData != null ? (
                    wantsDemoData ? (
                        <Alert
                            severity="info"
                            className={classes.demoChoiceNote}
                        >
                            Dummy data will be generated for this lease season.
                        </Alert>
                    ) : (
                        <Typography className={classes.demoChoiceNote}>
                            Demo data will not be generated for this lease
                            season.
                        </Typography>
                    )
                ) : null}
                {demoError ? (
                    <Typography
                        color="error"
                        className={classes.demoChoiceNote}
                    >
                        {demoError}
                    </Typography>
                ) : null}
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            className={classes.field}
                            label="Season Name"
                            variant="outlined"
                            size="small"
                            value={form.name}
                            onChange={(e) =>
                                updateForm({ name: e.target.value })
                            }
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            className={classes.field}
                            label="Occupancy Goal (%)"
                            variant="outlined"
                            size="small"
                            type="number"
                            inputProps={{ min: 1, max: 100 }}
                            value={form.goalPercent}
                            onChange={(e) => {
                                const value = Math.min(
                                    100,
                                    Math.max(1, Number(e.target.value) || 0),
                                )
                                updateForm({ goalPercent: value })
                            }}
                        />
                    </Grid>
                    {mode === 'edit' && !isCurrentSeason ? (
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={makeCurrent}
                                        onChange={(e) =>
                                            setMakeCurrent(e.target.checked)
                                        }
                                        color="primary"
                                    />
                                }
                                label="Set as current season"
                            />
                        </Grid>
                    ) : null}
                    <Grid item xs={12}>
                        <Box className={classes.termGroup}>
                            <FormControl
                                variant="outlined"
                                size="small"
                                className={classes.field}
                                required={mode === 'create'}
                            >
                                <InputLabel id="season-lease-term-label">
                                    Lease term
                                </InputLabel>
                                <Select
                                    labelId="season-lease-term-label"
                                    label="Lease term"
                                    value={form.leaseTermId ?? ''}
                                    onChange={(event) =>
                                        applyLeaseTerm(
                                            Number(event.target.value),
                                        )
                                    }
                                >
                                    {form.leaseTermId == null ? (
                                        <MenuItem value="">
                                            Select a lease term
                                        </MenuItem>
                                    ) : null}
                                    {leaseTerms.map((term) => (
                                        <MenuItem key={term.id} value={term.id}>
                                            {term.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <LeaseTermApplyFields
                                term={
                                    leaseTerms.find(
                                        (term) => term.id === form.leaseTermId,
                                    ) ?? null
                                }
                            />
                            <LeaseTermPropertyDetailsPrompt />
                        </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <KeyboardDatePicker
                            className={classes.field}
                            variant="inline"
                            autoOk
                            disableToolbar
                            inputVariant="outlined"
                            size="small"
                            clearable
                            label="Renewal deadline"
                            format="MM/dd/yyyy"
                            value={parseSeasonDate(form.renewalDeadline)}
                            minDateMessage=""
                            maxDateMessage=""
                            onChange={(date) =>
                                updateForm({
                                    renewalDeadline: seasonDateFromPicker(date),
                                })
                            }
                            helperText="Optional. After this date, renewal offers expire."
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField
                            className={classes.field}
                            label="Renewal goal (%)"
                            variant="outlined"
                            size="small"
                            type="number"
                            inputProps={{ min: 1, max: 100 }}
                            value={form.renewalGoalPercent ?? ''}
                            onChange={(e) => {
                                if (e.target.value === '') {
                                    updateForm({ renewalGoalPercent: null })
                                    return
                                }
                                const value = Math.min(
                                    100,
                                    Math.max(1, Number(e.target.value) || 0),
                                )
                                updateForm({ renewalGoalPercent: value })
                            }}
                            helperText="Optional."
                        />
                    </Grid>

                    {mode === 'create' ? (
                        <Grid item xs={12}>
                            <SeasonConfigSourcePanel
                                leasingSeasonConfigs={leasingSeasonConfigs}
                                leasingSeasonConfigId={
                                    form.leasingSeasonConfigId
                                }
                                onLeasingSeasonConfigIdChange={(configId) => {
                                    const selected = readyLeasingSeasonConfigs.find(
                                        (row) => row.id === configId,
                                    )
                                    updateForm({
                                        leasingSeasonConfigId: configId,
                                        leasingSeasonConfigName:
                                            selected?.name ?? null,
                                    })
                                }}
                            />
                        </Grid>
                    ) : (
                        <Grid item xs={12}>
                            {!bundleLocked &&
                            (readyLeasingSeasonConfigs.length > 0 ||
                                seasonHasPrivateCopy) ? (
                                <FormControl
                                    variant="outlined"
                                    size="small"
                                    className={classes.field}
                                >
                                    <InputLabel id="season-edit-lsc-label">
                                        Leasing Season Config
                                    </InputLabel>
                                    <Select
                                        labelId="season-edit-lsc-label"
                                        label="Leasing Season Config"
                                        value={form.leasingSeasonConfigId ?? ''}
                                        onChange={(event) => {
                                            const configId = Number(
                                                event.target.value,
                                            )
                                            const selected = readyLeasingSeasonConfigs.find(
                                                (row) => row.id === configId,
                                            )
                                            const keepingPrivateCopy =
                                                seasonHasPrivateCopy &&
                                                configId ===
                                                    form.leasingSeasonConfigId
                                            updateForm({
                                                leasingSeasonConfigId: configId,
                                                leasingSeasonConfigName:
                                                    selected?.name ??
                                                    (keepingPrivateCopy
                                                        ? form.leasingSeasonConfigName
                                                        : null),
                                            })
                                        }}
                                    >
                                        {seasonHasPrivateCopy &&
                                            form.leasingSeasonConfigId !=
                                                null && (
                                                <MenuItem
                                                    value={
                                                        form.leasingSeasonConfigId
                                                    }
                                                >
                                                    {form.leasingSeasonConfigName ??
                                                        'This season'}{' '}
                                                    · this season
                                                </MenuItem>
                                            )}
                                        {readyLeasingSeasonConfigs.map(
                                            (config) => (
                                                <MenuItem
                                                    key={config.id}
                                                    value={config.id}
                                                >
                                                    {config.bound_season?.name
                                                        ? `${config.name} · Used by ${config.bound_season.name}`
                                                        : config.name}
                                                </MenuItem>
                                            ),
                                        )}
                                    </Select>
                                </FormControl>
                            ) : (
                                <>
                                    <Typography
                                        variant="body2"
                                        color="textSecondary"
                                    >
                                        Leasing Season Config
                                    </Typography>
                                    <Typography variant="body1">
                                        {form.leasingSeasonConfigName ?? '—'}
                                    </Typography>
                                </>
                            )}
                            {seasonHasPrivateCopy && (
                                <Typography className={classes.pipelineNote}>
                                    This season has its own copy of{' '}
                                    {form.leasingSeasonConfigName ??
                                        'this config'}
                                    . Editing it leaves other seasons unchanged.
                                </Typography>
                            )}
                            {form.leasingSeasonConfigId != null && (
                                <Typography className={classes.pipelineNote}>
                                    <Link
                                        to={Routes.manage.leasingSeasonConfig.detail.replace(
                                            ':id',
                                            String(form.leasingSeasonConfigId),
                                        )}
                                    >
                                        {`Edit this season's fee, documents, pipeline, and Applicant Data`}
                                    </Link>
                                </Typography>
                            )}
                            {bundleLocked && (
                                <Typography className={classes.pipelineNote}>
                                    Locked — cannot change after the season has
                                    started.
                                </Typography>
                            )}
                        </Grid>
                    )}

                    {mode === 'create' && sortedSeasons.length > 0 && (
                        <Grid item xs={12}>
                            <SeasonLeadRolloverPanel
                                seasons={sortedSeasons}
                                enabled={rollLeadsEnabled}
                                onEnabledChange={setRollLeadsEnabled}
                                sourceSeasonId={rollSourceSeasonId}
                                onSourceSeasonIdChange={setRollSourceSeasonId}
                                selectedLeadIds={rollLeadIds}
                                onSelectedLeadIdsChange={setRollLeadIds}
                            />
                        </Grid>
                    )}
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isBusy}>
                    Cancel
                </Button>
                {mode === 'edit' && onSave && (
                    <Button
                        onClick={handleSave}
                        color="primary"
                        variant="contained"
                        disabled={isBusy || !canSubmit}
                    >
                        Save changes
                    </Button>
                )}
                {mode === 'create' && onCreate && (
                    <Button
                        onClick={handleCreate}
                        color="primary"
                        variant="contained"
                        disabled={isBusy || !canSubmit}
                    >
                        Create season
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    )
}
